/**
 * DAT – ATL-001438 (C5): reacts to a load action that covered, cancelled or
 * completed a load with an active DAT post (the server's `datCover`,
 * re-broadcast by the api interceptor as the `dat:cover` window event).
 *
 * - Always refreshes that load's DAT badge / grid cell.
 * - Automatic mode: a short info toast; the server is already removing it.
 * - Prompt mode: a warning toast with "Remove from DAT" (C4). Ignoring it
 *   leaves the post live; the badge keeps offering the removal.
 *
 * Mounted once inside the authenticated app; renders nothing.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../core/components/ui/ToastProvider';
import { useDatErrorMessage } from '../../core/components/dat/use-dat-error-message';
import { DAT_COVER_EVENT } from '../../lib/api';
import {
  useDatPostingEnabled,
  useDatPostingVisible,
  useInvalidatePosting,
  useRemoveDatPosting,
  type DatCoverEvent,
} from '../../hooks/use-dat-loadboard';

/** Long enough to act on, short enough not to pile up. */
const PROMPT_TOAST_MS = 15_000;

export function DatCoverListener(): null {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const datError = useDatErrorMessage();
  const visible = useDatPostingVisible();
  const canRemove = useDatPostingEnabled();
  const invalidate = useInvalidatePosting();
  const remove = useRemoveDatPosting();

  // The listener is registered once; read the latest values through a ref.
  const latest = useRef({ t, showToast, datError, visible, canRemove, invalidate, remove });
  latest.current = { t, showToast, datError, visible, canRemove, invalidate, remove };

  useEffect(() => {
    const onCover = (e: Event) => {
      const cover = (e as CustomEvent<DatCoverEvent>).detail;
      if (!cover?.loadId) return;
      const { t: tr, showToast: toast, datError: errText, visible: show, canRemove: can, invalidate: refresh, remove: rm } = latest.current;
      refresh(cover.loadId);
      if (!show || !cover.fresh) return;
      const reason = tr(`datLoadboard.cover.reason.${cover.reason}`);
      if (cover.mode === 'automatic') {
        toast(tr('datLoadboard.cover.toast.removing', { reason }), 'info');
        return;
      }
      toast(
        tr('datLoadboard.cover.toast.prompt', { reason }),
        'warning',
        PROMPT_TOAST_MS,
        can
          ? {
              label: tr('datLoadboard.cover.toast.action'),
              onClick: () =>
                rm.mutate(cover.loadId, {
                  onSuccess: () => toast(tr('datLoadboard.actions.toast.removed'), 'success'),
                  onError: (err) => toast(errText(err, 'datLoadboard.actions.toast.removeFailed'), 'error'),
                }),
            }
          : undefined,
      );
    };
    window.addEventListener(DAT_COVER_EVENT, onCover);
    return () => window.removeEventListener(DAT_COVER_EVENT, onCover);
  }, []);

  return null;
}

export default DatCoverListener;
